import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, useAnimationControls } from 'framer-motion';
import styles from '../styles/PageWrapper.scss';

const ChatMessage = ({
  isSentByUser, message, pulseAt,
  onTypingComplete, id, scrollToBottom,
}) => {
  const controls = useAnimationControls();
  const [displayedMessage, setDisplayedMessage] = useState(
    isSentByUser ? message : ''
  );

  useEffect(() => {
    if (!pulseAt) return;

    controls.start({
      opacity: [1, 0.5, 1, 0.5, 1, 0.5, 1],
      transition: {
        duration: 1.2,
        times: [0, 0.2, 0.4, 0.6, 1],
        ease: 'easeInOut',
      },
    });
  }, [pulseAt, controls]);

  useEffect(() => {
    if (isSentByUser) {
      setDisplayedMessage(message);
      scrollToBottom();
      return;
    }

    setDisplayedMessage('');

    let index = 0;
    const speed = 12;

    const interval = window.setInterval(() => {
      index += 1;
      setDisplayedMessage(message.slice(0, index));

      if (index >= message.length) {
        window.clearInterval(interval);
        onTypingComplete?.(id);
      }
    }, speed);

    // eslint-disable-next-line consistent-return -- clearInterval
    return () => window.clearInterval(interval);
  }, [message, isSentByUser, id, onTypingComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.1 } }}
      data-testid={isSentByUser ? 'user-message' : 'api-message'}
      className={`${isSentByUser ? 'flex-justify-end' : ''} flex width-full pad-1-tb`}
    >
      <motion.div
        animate={controls}
        className={`pad-2-lr body-3 ${styles.chatBubble} ${
          isSentByUser ? styles.userSentMessage : styles.aiSentMessage
        }`}
        style={{ originY: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {displayedMessage}
      </motion.div>
    </motion.div>
  );
};

export default ChatMessage;

ChatMessage.propTypes = {
  isSentByUser: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  pulseAt: PropTypes.string,
  onTypingComplete: PropTypes.func,
  id: PropTypes.number,
  scrollToBottom: PropTypes.func,
};
